import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getWatchlist, addWatch, removeWatch } from '../services/gradeWatchApi';

/**
 * GradeWatchContext — shared client state for the tickers a user watches for
 * grade-change alerts (Phase 2b). Loaded once for logged-in users so every
 * WatchButton across the app stays in sync without refetching. Toggling is
 * optimistic; a failed add (e.g. tier cap 403 → the global LimitReachedModal
 * fires from the api interceptor) rolls back.
 */
const TOKEN_KEY = 'alphagrade_auth_token';
export const GradeWatchContext = createContext(null);

const isLoggedIn = () => !!localStorage.getItem(TOKEN_KEY);

export function GradeWatchProvider({ children }) {
  const [watched, setWatched] = useState(() => new Set());
  const [limit, setLimit] = useState(0);
  const [tierName, setTierName] = useState('');
  const [loaded, setLoaded] = useState(false);

  const refresh = useCallback(async () => {
    if (!isLoggedIn()) {
      setWatched(new Set());
      setLoaded(true);
      return;
    }
    try {
      const data = await getWatchlist();
      setWatched(new Set((data.watched || []).map((s) => s.toUpperCase())));
      setLimit(data.limit || 0);
      setTierName(data.tier || '');
    } catch (e) {
      // Non-fatal (e.g. offline). Leave prior state; buttons just won't reflect.
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const isWatched = useCallback(
    (symbol) => watched.has((symbol || '').toUpperCase()),
    [watched]
  );

  const toggle = useCallback(
    async (symbol) => {
      const sym = (symbol || '').toUpperCase();
      if (!sym || !isLoggedIn()) return;
      const wasWatched = watched.has(sym);

      // Optimistic update.
      setWatched((prev) => {
        const next = new Set(prev);
        if (wasWatched) next.delete(sym);
        else next.add(sym);
        return next;
      });

      try {
        const data = wasWatched ? await removeWatch(sym) : await addWatch(sym);
        setWatched(new Set((data.watched || []).map((s) => s.toUpperCase())));
        if (data.limit) setLimit(data.limit);
      } catch (err) {
        // Roll back (tier-cap modal, if any, is already handled by the api layer).
        setWatched((prev) => {
          const next = new Set(prev);
          if (wasWatched) next.add(sym);
          else next.delete(sym);
          return next;
        });
      }
    },
    [watched]
  );

  const value = {
    watched,
    isWatched,
    toggle,
    refresh,
    count: watched.size,
    limit,
    tierName,
    loaded,
    loggedIn: isLoggedIn(),
  };

  return <GradeWatchContext.Provider value={value}>{children}</GradeWatchContext.Provider>;
}

export function useGradeWatch() {
  const ctx = useContext(GradeWatchContext);
  if (!ctx) throw new Error('useGradeWatch must be used within a GradeWatchProvider');
  return ctx;
}
